import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Formik } from 'formik';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Container } from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import Alert from 'src/components/Alert';
import { CONSTANTS, STRINGS } from 'src/commons';
import { commonReducerAction } from 'src/redux/reducers/common';
import { loaderReducerAction } from 'src/redux/reducers/loader';
import { paymentReducerAction } from 'src/redux/reducers/payment';
import { getPayment, updatePaymentHistory } from 'src/api/payment';
import PaymentForm from 'src/components/payments/PaymentFrom';

const UpdatePayment = () => {
  dayjs.extend(customParseFormat);
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: '',
    severity: ''
  });
  const { startLoading, stopLoading } = loaderReducerAction;
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.common);
  const { updatePayment } = paymentReducerAction;
  const { logoutUser } = commonReducerAction;
  const { paymentList } = useSelector((state) => state.payment);
  const [payment, setPayment] = useState({
    amount: '',
    date: '',
    payment_month_year: '',
    mode_of_payment: 0
  });

  const location = useLocation();
  const _id = location?.pathname?.split('/')?.pop();

  useEffect(() => {
    if (paymentList?.length) {
      const payment = paymentList.find((payment) => payment?._id === _id);
      if (payment) {
        setPayment(payment);
      }
    } else {
      dispatch(startLoading());
      getPayment(user)
        .then((res) => {
          if (res.status === CONSTANTS.STATUS_CODES.FORBIDDEN)
            dispatch(logoutUser());
          if (res.status === CONSTANTS.STATUS_CODES.SUCCESS) {
            dispatch(updatePayment(res.data));
          }
          dispatch(stopLoading());
        })
        .catch((err) => {
          console.log(err);
          dispatch(stopLoading());
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentList]);

  const navigate = useNavigate();
  const date = dayjs().format('YYYY-MM-DD');

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackbarState({
      ...snackbarState,
      open: false
    });
  };
  return (
    <>
      <Helmet>
        <title>Payment | GMS</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          marginTop: '2rem'
        }}
      >
        <Container maxWidth="sm">
          <Formik
            initialValues={{
              amount: payment?.amount,
              payment_for_this_month: 
                dayjs(`${payment?.payment_month_year.split("-")[0]}-${payment?.payment_month_year}`).format("YYYY-MM-DD"),
              payment_received_on: dayjs(payment?.date).format("YYYY-MM-DD"),
              mode_of_payment: payment?.mode_of_payment
            }}
            enableReinitialize
            validationSchema={CONSTANTS.registerPaymentScheme}
            onSubmit={async (values) => {
              const result = {
                member_id: payment.member_id,
                amount: values.amount,
                date: dayjs(values.payment_received_on).valueOf(),
                payment_month_year: dayjs(values.payment_for_this_month).format(
                  'MM-DD-YYYY'
                ),
                mode_of_payment: values.mode_of_payment
              };
              try {
                dispatch(startLoading());
                const res = await updatePaymentHistory(
                  user,
                  result,
                  payment?._id
                );
                dispatch(stopLoading());

                const isError = res.status !== 200;
                setSnackbarState({
                  open: true,
                  message: res.message,
                  severity: isError ? 'error' : 'success'
                });
                if (!isError) {
                  navigate(
                    CONSTANTS.ROUTES.PRIVATE_ROUTES.PAYMENT +
                      '/' +
                      payment.member_id
                  );
                }
              } catch (err) {
                dispatch(stopLoading());
                setSnackbarState({
                  open: true,
                  message: err.message,
                  severity: 'error'
                });
              }
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              setFieldValue,
              isSubmitting,
              touched,
              values
            }) => (
              <PaymentForm
                errors={errors}
                handleBlur={handleBlur}
                handleChange={handleChange}
                handleSubmit={handleSubmit}
                setFieldValue={setFieldValue}
                isSubmitting={isSubmitting}
                touched={touched}
                values={values}
                heading={STRINGS.UPDATE_PAYMENT}
                isFromUpdateBatch={true}
              />
            )}
          </Formik>
          <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            open={snackbarState.open}
            autoHideDuration={3000}
            onClose={handleClose}
          >
            <div>
              <Alert onClose={handleClose} severity={snackbarState.severity}>
                {snackbarState.message}
              </Alert>
            </div>
          </Snackbar>
        </Container>
      </Box>
    </>
  );
};

export default UpdatePayment;
